import type { FloorPlanMarkup } from '../../image-annotator/types';
import type { CustomField, RichLead } from '../../lead.popup/types';
import type {
  AdditionalContact,
  Company,
  Contact,
  Deal,
  ImageItem,
  Lead,
  Opportunity,
  Section,
  SimpleHouse,
  SimpleProject,
  Unit,
  UnitStatusType,
  User,
  WepsterMessenger,
} from '../../types';
import type { Booking } from './bookingApi';
import type { Interest } from './interestApi';

import { cache } from '../cache';
import { type ApiResponse, estClient } from './estClient';
import { NotFoundError } from './httpClient';
import {
  fixBooking,
  fixInterest,
  fixLead,
  fixOpportunity,
  fixUnit,
} from './normalizationFns';

class UnitApi {
  async getUnitWithDeps(id: string): Promise<FeUnit | null> {
    try {
      const feUnit = await estClient.get<ApiResponse<FeUnit>>(
        `/api/fe/unit/${id}`,
      );

      // some of our APIs can not handle new number ids
      // convert back to string for compatibility

      fixUnit(feUnit.unit);

      for (const lead of feUnit.leads) {
        fixLead(lead);
      }

      for (const opportunity of feUnit.opportunities) {
        fixOpportunity(opportunity);
      }

      for (const booking of feUnit.bookings) {
        fixBooking(booking);
      }

      for (const interest of feUnit.interests) {
        fixInterest(interest);
      }

      return feUnit;
    } catch (error) {
      if (error instanceof NotFoundError) {
        return null;
      }

      throw error;
    }
  }

  async getById(id: string): Promise<LegacyRichUnit | null> {
    try {
      return estClient.get<LegacyRichUnit>(`/api/unit/get/${id}`);
    } catch (error) {
      if (error instanceof NotFoundError) {
        return null;
      }

      throw error;
    }
  }

  async getByIdSecure(id: string): Promise<SecureUnit | null> {
    try {
      return estClient.get<SecureUnit>(`/api/unit/get_secure/${id}`);
    } catch (error) {
      if (error instanceof NotFoundError) {
        return null;
      }

      throw error;
    }
  }

  async getStatuses(): Promise<UnitStatus[]> {
    return cache.get('unit-statuses', () =>
      estClient.get<UnitStatus[]>('/api/unit/status'),
    );
  }

  async updateStatus(
    id: LegacyRichUnit['unit_id'],
    body: {
      status_id: LegacyRichUnit['status_id'];
    },
  ): Promise<LegacyRichUnit | null> {
    try {
      return estClient.post<LegacyRichUnit>(`/api/unit/${id}/update_status`, {
        body,
      });
    } catch (error) {
      if (error instanceof NotFoundError) {
        return null;
      }

      throw error;
    }
  }

  async getCustomFields(): Promise<CustomField[]> {
    return cache.get('unit-custom-fields', () =>
      estClient.get<CustomField[]>('/api/unit/custom_fields'),
    );
  }

  async updateCustomFields(
    unitId: RichUnit['unit_id'],
    customFields: { [id: string]: string },
  ): Promise<void> {
    await estClient.post(`/api/unit/${unitId}/custom_fields`, {
      body: { custom_fields: customFields },
    });
  }

  async updateParams(
    id: string,
    body: UnitParams,
  ): Promise<LegacyRichUnit | null> {
    try {
      return estClient.post<LegacyRichUnit>(`/api/unit/${id}/update_params`, {
        body,
      });
    } catch (error) {
      if (error instanceof NotFoundError) {
        return null;
      }

      throw error;
    }
  }

  async bookUnit({
    unitId,
    leadId,
    statusId,
    days,
  }: {
    unitId: LegacyRichUnit['unit_id'];
    leadId: UnitInterestedLead['id'];
    statusId: UnitStatus['id'];
    days: UnitStatus['days'];
  }) {
    await estClient.post(`/api/unit/${unitId}/booking`, {
      body: {
        lead_id: leadId,
        days,
        status_id: statusId,
      },
    });
  }

  async updateResale({
    unitId,
    wasResold,
  }: {
    unitId: LegacyRichUnit['unit_id'];
    wasResold: boolean;
  }): Promise<void> {
    await estClient.post<LegacyRichUnit>(`/api/unit/${unitId}/update_resale`, {
      body: {
        is_in_resale: wasResold ? 1 : 0,
      },
    });
  }

  async fastBook(unitId: string): Promise<void> {
    const statuses = await this.getStatuses();

    let targetStatus: UnitStatus | null = null;

    for (const status of statuses) {
      if (status.type !== unitApi.STATUS_TYPE.BOOKED) {
        continue;
      }

      if (!targetStatus || Number(status.days) < Number(targetStatus.days)) {
        // looking for the minimum booking period
        targetStatus = status;
      }
    }

    if (!targetStatus) {
      throw new Error('Can not find appropriate status for fast booking');
    }

    await estClient.post<void>(`/api/unit/${unitId}/fast_booking`, {
      body: {
        status_id: targetStatus.id,
        days: targetStatus.days,
      },
    });
  }

  async fastDeal(unitId: string): Promise<void> {
    const statuses = await this.getStatuses();

    let targetStatus: UnitStatus | null = null;

    for (const status of statuses) {
      if (status.type !== unitApi.STATUS_TYPE.SOLD) {
        continue;
      }

      targetStatus = status;
      break;
    }

    if (!targetStatus) {
      throw new Error('Can not find appropriate status for fast deal');
    }

    await estClient.post<void>(`/api/unit/${unitId}/fast_sale`, {
      body: {
        status_id: targetStatus.id,
      },
    });
  }

  async fastNotInStock(unitId: string): Promise<void> {
    const statuses = await this.getStatuses();

    let targetStatus: UnitStatus | null = null;

    for (const status of statuses) {
      if (status.type !== unitApi.STATUS_TYPE.CLOSED) {
        continue;
      }

      targetStatus = status;
      break;
    }

    if (!targetStatus) {
      throw new Error('Can not find appropriate status for fast not in stock');
    }

    // updateStatus won't work here because we must be sure that we have removed
    // previous fast book/deal statuses if any
    await estClient.post<void>(`/api/unit/${unitId}/fast_clear`, {
      body: {
        status_id: targetStatus.id,
      },
    });
  }

  /**
   * Cancels fast deal/booking. Clean ups the state introduced by fast operation
   */
  async fastClear(unitId: string): Promise<void> {
    const statuses = await this.getStatuses();

    let targetStatus: UnitStatus | null = null;

    for (const status of statuses) {
      if (status.type !== unitApi.STATUS_TYPE.AVAILABLE) {
        continue;
      }

      targetStatus = status;
      break;
    }

    if (!targetStatus) {
      throw new Error('Can not find appropriate status for fast clear');
    }

    await estClient.post<void>(`/api/unit/${unitId}/fast_clear`, {
      body: {
        status_id: targetStatus.id,
      },
    });
  }

  async createProspect({
    unitId,
    ...body
  }: {
    unitId: string;
    name: string;
    phone: string;
  }): Promise<{ message?: 'prospect exist' | string }> {
    return await estClient.post(`/api/prospect/create`, {
      body: {
        unit_id: unitId,
        ...body,
      },
    });
  }

  async getDocsDirUrl(unitId: string) {
    const resp = await estClient.get<GoogleFolderResponse>(
      `/api/unit/${unitId}/google_folder`,
    );

    return `https://drive.google.com/drive/u/0/folders/${resp.folder.id}`;
  }

  /**
   * @deprecated in favor of /fe/unit/:id request
   */
  async getInterestedLeads(unitId: string): Promise<UnitInterestedLead[]> {
    const leads = await estClient.get<UnitInterestedLead[]>(
      `/api/unit/${unitId}/interested_leads`,
    );

    // some of our APIs can not handle new number ids
    // convert back to string for compatibility
    for (const lead of leads) {
      fixLead(lead);
    }

    return leads;
  }

  async addInterest({
    unitId,
    leadId,
  }: {
    unitId: string;
    leadId: UnitInterestedLead['id'];
  }): Promise<void> {
    await estClient.post<void>(`/api/unit/${unitId}/interest`, {
      body: {
        lead_id: leadId,
      },
    });
  }

  async getUnitsForSection(sectionId: string): Promise<Unit[]> {
    const result = await estClient.get<Record<string, Unit>>(
      `/api/v1/unit/section/${sectionId}/asObject`,
    );

    return Object.values(result);
  }

  /**
   * Binds/unbinds an image to many units in one request
   */
  async bindImageBulk({
    imageId,
    imageType,
    bindUnitIds = [],
    unbindUnitIds = [],
  }: {
    imageId: string;
    imageType: UnitImage['image_type'];
    bindUnitIds?: string[];
    unbindUnitIds?: string[];
  }): Promise<{ added: string[]; removed: string[] }> {
    return estClient.post('/api/unit/bind_image_bulk', {
      body: {
        image_id: imageId,
        image_type: imageType,
        bind_unit_ids: bindUnitIds,
        unbind_unit_ids: unbindUnitIds,
      },
    });
  }

  get STATUS_TYPE() {
    return {
      CLOSED: '1',
      AVAILABLE: '2',
      BOOKED: '3',
      SOLD: '4',
    } as const;
  }

  get IMAGE_TYPE() {
    return {
      MASTER_PLAN: '1',
      FLOOR_PLAN: '2',
      UNIT_PLAN: '3',
      UNIT_VISUAL: '4',
    } as const;
  }

  get TYPES() {
    return {
      NOTHING: '1',
      APT: '2',
      OFFICE: '3',
      VILLA: '4',
      PLOT: '5',
      TOWNHOUSE: '6',
      HOTEL_TYPE: '7',
      STOREROOM: '8',
      ATTIC: '9',
      PARKING_LOT: '10',
      PAVEMENT_PARKING: '11',
      DUPLEX: '12',
      COTTAGE: '13',
    } as const;
  }
}

export interface UnitStatus {
  id: string;
  name: string;

  type: UnitStatusType;

  /**
   * How many days should be booked (for booking related statuses)
   */
  days: string;
  lock: '1' | '0';
  created_at: string | null;
}

export interface UnitImage {
  id: string;
  unit_id: string;
  image_id: string;

  /**
   * '1' - MASTER_PLAN
   * '2' - FLOOR_PLAN
   * '3' - UNIT_PLAN
   * '4' - UNIT_VISUAL
   */
  image_type: '1' | '2' | '3' | '4';

  image: ImageItem;

  /**
   * Floor plan polygon markup bound to `image_id` - only set when
   * `image_type` is FLOOR_PLAN and a markup was saved for it, null otherwise
   */
  markup: FloorPlanMarkup | null;
}

interface GoogleFolderResponse extends ApiResponse {
  code: string;

  folder: {
    /**
     * Folder id in google drive
     */
    id: string;

    /**
     * A path to the folder. Can be shown to user but useless when creating a link to google drive
     */
    path: string;
  };
}

export type FeUnit = {
  unit: RichUnit;

  /**
   * Seems to be the same as unit.custom.typeLabel in the legacy format
   *
   * @example 'Apt.'
   */
  unit_type_name: string;
  unit_status: UnitStatus;
  unit_image_assns: UnitImage[];
  unit_labels: UnitLabel[];

  /**
   * Currency rates
   *
   * @example { "UAH/USD": "42.0653700000" }
   */
  standard_exchange_rates: { [currencyPair: string]: string };
  interests: Interest[];
  bookings: Booking[];
  deals: Deal[];
  leads: RichLead[];
  opportunities: Opportunity[];
  sections: Section[];
  houses: SimpleHouse[];
  objects: SimpleProject[];
  users: User[];
};

export type RichUnit = Omit<
  Unit,
  // some unit fields that have different types
  'status' | 'status_type' | 'custom' | 'days' | 'lock' | 'name'
> & {
  price_assn_id: number;

  /**
   * @example "2021-08-23"
   */
  price_started_from: string;
  custom_fields: CustomFieldValue[];

  /**
   * If the time delta with now is around 100 years (just don't ask why) — this is Deal otherwise Booking
   *
   * @example '2017-08-08 22:23:33'
   */
  fast_deal_due_date: string | null;
};

/**
 * Full unit info as returned from /api/unit/get/:id
 */
export interface LegacyRichUnit
  extends Omit<
    Unit,
    // some unit fields that have different types
    'status_id' | 'status_type' | 'days' | 'lock'
  > {
  status_id: string;
  status_type: string;
  days: string;
  lock: '0' | '1';

  price_assn_id: string;
  /**
   * @example '2024-10-10'
   */
  price_started_from: string;

  /**
   * Status id
   * @deprecated
   */
  id: string;

  company: Company;

  section: Section & {
    house: SimpleHouse;
  };

  /**
   * Currency rates
   *
   * @example { "UAH/USD": "42.0653700000" }
   */
  standard_exchange_rates: { [currencyPair: string]: string };

  unit_images: UnitImage[];

  labels: UnitLabel[];

  tasks: never[];
}

export interface SecureUnit {
  unit_id: string;
  custom_fields: Pick<CustomFieldValue, 'name' | 'value'>[];
  status_lock: 0 | 1;
  lead_unit_assn?: {
    id: string;
    unit_id: string;
    lead_id: string;
    deal_id: string;

    /**
     * @example '2017-08-08 22:23:33'
     */
    created_at: string;
    created_by: string;

    /**
     * @example '2017-08-08 22:23:33'
     */
    updated_at: string;
    updated_by: string;
    status: '1';
    type: '1' | '2' | '3';

    /**
     * The date the unit is booked till
     *
     * @example '2017-08-08 22:23:33'
     */
    due_date: string | null;

    is_fast_deal: '0' | '1';

    deal?: Deal;
    booking?: Booking;
    manager: User;
    lead: Lead;
  };
}

interface CustomFieldValue {
  id: string;
  field_id: string;
  name: string;
  value: string;
}

export type UnitLabel = {
  id: string;
  label_id: string;
  unit_id: string;
  created_at: string;
  label: {
    id: string;
    name: string;

    /**
     * Abbreviated name
     */
    mark: string;
    created_at: string | null;
  };
};

export type UnitParams = {
  area_total: string;
  is_price_per_meter: number | 0 | 1;
  price: string;
  currency: string | null;
  exchange_rate_declared: string | null;
};

export interface UnitInterestedLead {
  id: string;
  status_id: string;
  last_status: string;

  name: string | null;
  phone: string;
  email: string | null;
  note: string | '';
  contacts: Contact[];
  additional_contacts: AdditionalContact[];

  /**
   * @example '2025-12-26 19:22:10'
   */
  status_last_update_at: string;
  status_last_updated_by: string;

  /**
   * @example '2025-12-26 19:22:10'
   */
  created_at: string;
  created_by: string;

  /**
   * @example '2025-12-26 19:22:10'
   */
  updated_at: string;
  updated_by: string;

  /**
   * @example '2025-12-26 19:22:10'
   */
  merged_at: string | null;
  merged_into: string | null;

  reservations: '0';
  reservations_active: '0';
  reservations_inactive: '0';
  purchases: '0';
  purchases_active: '0';
  purchases_inactive: '0';
  interested: '1';

  lead_creation_source: string;

  /**
   * Relation to AppealSource['id']
   */
  ad_source_id: string | null;
  appeal_on_create_id: string;

  wepster_id: string | null;
  wepster_messenger: WepsterMessenger | null;
  wepster_contact: string | null;

  lua_id: string;

  /**
   * @example '2025-12-26 19:22:10'
   */
  lua_created_at: string;
  lua_created_by: string | null;
  lua_created_by_user: User | null;
  custom_fields: unknown[];

  is_deleted: '1' | '0';

  /**
   * Something related to $rootScope.embeddedIntegrationName
   *
   * @deprecated
   */
  integration?: {
    deal: {
      status: {
        name: string;
      };
    };
  };
}

export const unitApi = new UnitApi();
